import { useEffect, useMemo, useState, useCallback } from "react";
import { ACTIVITIES, COLLECTIONS } from "./data.js";
import { fetchManifest, photoUrl } from "./api.js";
import { listDriveFolder } from "./driveSource.js";
import { fetchAutomaticActivities, mergeActivities } from "./activityFeed.js";
import { driveFolderUrl } from "./utils/drive.js";
import Detail from "./components/Detail.jsx";
import Moments from "./components/Moments.jsx";
import UnifiedArchive from "./components/UnifiedArchive.jsx";

const COHORT_LABEL = { south: "南班", north: "北班", all: "全班" };

function normalizeR2Photo(p) {
  return {
    ...p,
    source: "r2",
    thumb: photoUrl(p.thumb),
    medium: photoUrl(p.medium),
    original: photoUrl(p.original),
  };
}

function samplePreviewPhotos(photos, count = 3) {
  if (!photos?.length) return [];
  const pool = [...photos];
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return pool.slice(0, count);
}

export default function App() {
  const [automaticActivities, setAutomaticActivities] = useState([]);
  const [automaticActivitiesLoaded, setAutomaticActivitiesLoaded] =
    useState(false);
  const sorted = useMemo(
    () => mergeActivities(ACTIVITIES, automaticActivities),
    [automaticActivities],
  );
  const activityPreviewPhotos = useMemo(
    () =>
      Object.fromEntries(
        sorted.map((a) => [a.id, samplePreviewPhotos(a.previewPhotos)]),
      ),
    [sorted],
  );
  const [activeId, setActiveId] = useState(null);
  const [manifest, setManifest] = useState({
    activities: {},
    version: 1,
    updatedAt: null,
  });
  const [drivePhotosByCollection, setDrivePhotosByCollection] = useState({});
  const [activeCollectionId, setActiveCollectionId] = useState(null);

  const loadManifest = useCallback(
    async () => setManifest(await fetchManifest()),
    [],
  );
  const loadDrive = useCallback(async () => {
    const entries = await Promise.all(
      COLLECTIONS.filter((c) => c.driveFolderId).map(async (c) => [
        c.id,
        await listDriveFolder(c.driveFolderId),
      ]),
    );
    setDrivePhotosByCollection(Object.fromEntries(entries));
  }, []);

  useEffect(() => {
    loadManifest();
    loadDrive();
  }, [loadManifest, loadDrive]);
  useEffect(() => {
    fetchAutomaticActivities(setAutomaticActivities)
      .then(setAutomaticActivities)
      .catch(() => setAutomaticActivities([]))
      .finally(() => setAutomaticActivitiesLoaded(true));
  }, []);

  const mergedPhotosByCollection = useMemo(() => {
    const out = {};
    for (const c of COLLECTIONS) {
      const r2 = (manifest.activities?.[c.id]?.photos || []).map(
        normalizeR2Photo,
      );
      out[c.id] = [...r2, ...(drivePhotosByCollection[c.id] || [])];
    }
    return out;
  }, [manifest, drivePhotosByCollection]);

  const activeActivity = sorted.find((a) => a.id === activeId);
  const activeIdx = sorted.findIndex((a) => a.id === activeId);
  const prev = activeIdx > 0 ? sorted[activeIdx - 1] : null;
  const next =
    activeIdx >= 0 && activeIdx < sorted.length - 1
      ? sorted[activeIdx + 1]
      : null;
  const activeCollection =
    COLLECTIONS.find((c) => c.id === activeCollectionId) || null;
  const overlayOpen = Boolean(activeActivity || activeCollection);

  useEffect(() => {
    document.body.style.overflow = overlayOpen ? "hidden" : "";
    if (overlayOpen) window.scrollTo(0, 0);
    return () => {
      document.body.style.overflow = "";
    };
  }, [overlayOpen]);

  const moments = COLLECTIONS[0] || null;
  const momentsPhotos = (moments && mergedPhotosByCollection[moments.id]) || [];
  const momentsEnabled = Boolean(
    moments && (moments.driveFolderId || momentsPhotos.length),
  );
  const momentsCluster = momentsPhotos.slice(0, 4);

  return (
    <>
      <UnifiedArchive
        activities={sorted}
        automaticActivitiesLoaded={automaticActivitiesLoaded}
        activityPreviewPhotos={activityPreviewPhotos}
        moments={moments}
        momentsEnabled={momentsEnabled}
        momentsCluster={momentsCluster}
        onOpenActivity={setActiveId}
      />
      {activeActivity && (
        <Detail
          activity={activeActivity}
          prev={prev}
          next={next}
          onClose={() => setActiveId(null)}
          onNav={setActiveId}
        />
      )}
      {activeCollection && (
        <Moments
          collection={activeCollection}
          photos={mergedPhotosByCollection[activeCollection.id] || []}
          onClose={() => setActiveCollectionId(null)}
          onUploaded={() => {
            loadManifest();
            loadDrive();
          }}
        />
      )}
    </>
  );

  /* Legacy home retained temporarily as a safe fallback while the unified archive is deployed. */
  // eslint-disable-next-line no-unreachable
  return (
    <div className="app">
      <div className="page">
        <header className="masthead">
          <div className="brand-row">
            <span>E118</span>
            <span className="dot" />
            <span>Class Activities</span>
            <span className="dot" />
            <span>Vol. I</span>
          </div>
          <h1>
            一起走過的，
            <br />
            慢慢地，
            <br />
            <span className="hl">記住。</span>
          </h1>
          <p className="lead">
            成大 EMBA 第 E118 級。
            <br />
            我們從不同的城市走來，在某個夜裡，
            <br />
            發現已坐在彼此的長桌前——
            <br />
            那些不會被寫進論文的時光，
            <br />
            才是這兩年最值得帶走的部分。
          </p>
          <div className="masthead-meta">
            <div className="key">班級</div>
            <div className="val">E118．南班 ＋ 北班</div>
            <div className="key">記錄</div>
            <div className="val">由全班共同維護．照片陸續上傳中</div>
          </div>
        </header>
        <section className="cards-block">
          <div className="index-label">
            <span>活動索引</span>
            <span>
              {automaticActivitiesLoaded
                ? `${sorted.length} 場${momentsEnabled ? " ＋ 散影" : ""}`
                : "同步活動載入中…"}
            </span>
          </div>
          {sorted.map((a, i) => {
            const driveUrl = driveFolderUrl(a.driveFolderId);
            const previews = activityPreviewPhotos[a.id] || [];
            return (
              <div className="act-card" key={a.id}>
                <button
                  type="button"
                  className="act-card-hit"
                  onClick={() => setActiveId(a.id)}
                >
                  <div className="act-eyebrow">
                    {i === 0 && (
                      <>
                        <span className="tag-new">最新</span>
                        <span className="sep" />
                      </>
                    )}
                    <span>{a.dateLabel}</span>
                    {a.cohortBand && COHORT_LABEL[a.cohortBand] && (
                      <>
                        <span className="sep" />
                        <span>{COHORT_LABEL[a.cohortBand]}</span>
                      </>
                    )}
                  </div>
                  <h3>{a.title}</h3>
                  {a.location && <div className="act-loc">{a.location}</div>}
                </button>
                {driveUrl ? (
                  <a
                    className="act-drive-link"
                    href={driveUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span>查看活動照片</span>
                    <span className="act-drive-sub">Google 雲端硬碟 →</span>
                  </a>
                ) : (
                  <span className="act-drive-link act-drive-link-disabled">
                    照片尚未提供
                  </span>
                )}
                {previews.length > 0 && (
                  <div
                    className="act-preview"
                    aria-label={`${a.title} 精選照片`}
                  >
                    {previews.map((src) => (
                      <img
                        key={src}
                        src={src}
                        alt={`${a.title} 活動照片`}
                        loading="lazy"
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {momentsEnabled && (
            <button
              className="act-card moments-card"
              onClick={() => setActiveCollectionId(moments.id)}
            >
              <div className="act-eyebrow">
                <span>散影</span>
                <span className="sep" />
                <span>Moments</span>
              </div>
              <h3>{moments.tagline}</h3>
              <div className="act-loc">{moments.blurb}</div>
              <div className="act-cluster">
                {momentsCluster.length > 0
                  ? momentsCluster.map((p) => (
                      <div className="pc" key={p.id}>
                        <img
                          src={p.thumb}
                          alt=""
                          loading="lazy"
                          referrerPolicy="no-referrer"
                        />
                      </div>
                    ))
                  : [0, 1, 2].map((k) => <div className="pc pc-ph" key={k} />)}
                <span className="act-more">進去看 →</span>
              </div>
            </button>
          )}
        </section>
        <footer className="page-footer">
          <div className="foot-row">
            <span>NCKU · EMBA · E118</span>
            <span>Class Activities</span>
          </div>
          <div className="foot-meta">
            E118 全班共同紀錄．每張照片都是一個現場．
          </div>
        </footer>
      </div>
      {activeActivity && (
        <Detail
          activity={activeActivity}
          prev={prev}
          next={next}
          onClose={() => setActiveId(null)}
          onNav={setActiveId}
        />
      )}
      {activeCollection && (
        <Moments
          collection={activeCollection}
          photos={mergedPhotosByCollection[activeCollection.id] || []}
          onClose={() => setActiveCollectionId(null)}
          onUploaded={() => {
            loadManifest();
            loadDrive();
          }}
        />
      )}
    </div>
  );
}
